<main class="bg-black text-white min-h-screen overflow-x-hidden">

  <!-- Hero -->
  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-3xl">
        <a routerLink="/products/connectiq" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          VC SCORE <span class="text-varroc-accent">Driver Intelligence</span> &amp; Analytics
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-2xl" data-aos="fade-up" data-aos-delay="200">
          VC-SCORE is an advanced event detection and scoring engine that transforms raw telematics data into actionable driver intelligence enabling safe driving programs, usage-based insurance, and fleet performance benchmarking.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all vc_ score_driver_intelligence_analytics_request_demo_button">
            Request Demo
          </a>
       
        </div>
      </div>
    </div>
  </section>

  <!-- Sticky Nav -->
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <!-- Content -->
  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">

    <!-- Overview -->
    <section id="overview" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">What Is VC-SCORE</span>
          <h2 class="text-4xl font-black mt-3 mb-5">Turn Raw Telematics into Driver Insight</h2>
          <p class="text-varroc-text-gray leading-relaxed mb-5">VC-SCORE processes continuous telemetry streams from connected vehicles to detect driving behaviour events in real time. Each event is classified, weighted, and aggregated into composite scores that reflect a driver's overall safety and efficiency profile.</p>
          <p class="text-varroc-text-gray leading-relaxed">These scores power OEM safety programs, enable insurance products, support driver coaching flows, and provide the data backbone for fleet performance management at any scale.</p>
        </div>
        <div class="grid grid-cols-2 gap-4" data-aos="fade-left">
          <div class="feature-card text-center">
            <i class='bx bx-radar icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Real-Time Detection</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-stats icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Composite Scoring</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-file icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">UBI-Ready</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-bar-chart-alt-2 icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Fleet Analytics</div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Event Detection -->
    <section id="events" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Intelligent Detection</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Driving Events Detected</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">VC-SCORE monitors a comprehensive battery of driving event types across safety, efficiency, and risk dimensions.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div *ngFor="let event of events; let i = index" class="feature-card" data-aos="fade-up" [attr.data-aos-delay]="i * 50">
          <div class="w-10 h-10 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-xl' [class]="event.icon"></i>
          </div>
          <h3 class="text-white font-semibold text-sm mb-2">{{ event.label }}</h3>
          <p class="text-varroc-text-gray text-xs leading-relaxed">{{ event.desc }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Use Cases -->
    <section id="usecases" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Applications</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Primary Use Cases</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">VC-SCORE powers diverse downstream applications across OEM, insurance, and fleet domains.</p>
      </div>
      <div class="grid md:grid-cols-2 gap-6">
        <div *ngFor="let uc of useCases; let i = index" class="feature-card flex items-start gap-5" data-aos="fade-up" [attr.data-aos-delay]="i * 80">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <i class='bx icon-gradient text-2xl' [class]="uc.icon"></i>
          </div>
          <div>
            <h3 class="text-white font-bold text-lg mb-2">{{ uc.title }}</h3>
            <p class="text-varroc-text-gray text-sm leading-relaxed">{{ uc.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Scoring Engine -->
    <section id="scoring" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Scoring Engine</span>
          <h2 class="text-4xl font-black mt-3 mb-5">Configurable, Transparent Scoring</h2>
          <p class="text-varroc-text-gray leading-relaxed mb-6">The scoring engine is fully configurable per OEM deployment — allowing custom event weights, threshold calibration, and scoring window definitions that align to specific product requirements and market contexts.</p>
        </div>
        <div class="feature-card" data-aos="fade-left">
          <h3 class="text-white font-bold text-lg mb-4">Engine Capabilities</h3>
          <ul class="space-y-3">
            <li *ngFor="let point of scoringPoints" class="flex items-start gap-3 text-varroc-text-gray text-sm">
              <i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i>
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="workflow" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Workflow</span>
        <h2 class="text-4xl font-black mt-2 mb-4">VC-SCORE Workflow</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">This vertical workflow diagram shows how telemetry ingestion, driving event detection, scoring logic, downstream actions, and business applications connect across the VC-SCORE intelligence layer.</p>
      </div>
      <div class="flex justify-center" data-aos="fade-up">
        <img [src]="workflowImage" alt="VC-SCORE workflow diagram" class="w-full max-w-8xl h-auto rounded-xl block">
      </div>
    </section>

    <!-- CTA -->
    <!-- <section class="text-center py-16 bg-gradient-to-r from-varroc-accent/10 via-varroc-dark to-varroc-accent/10 border border-varroc-accent/20 rounded-3xl" data-aos="fade-up">
      <h2 class="text-4xl font-black mb-4">Build Smarter Products with VC-SCORE</h2>
      <p class="text-varroc-text-gray text-lg mb-8 max-w-2xl mx-auto">Enable UBI, safety programs, and fleet intelligence powered by real-time telematics scoring. Deploy in weeks, not months.</p>
      <div class="flex flex-wrap gap-4 justify-center">
        <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-10 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all text-lg">Talk to Team</a>
        <a routerLink="/products/connectiq" class="border border-varroc-accent/40 font-semibold px-10 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all text-lg">Back to ConnectIQ</a>
      </div>
    </section> -->

  </div>
</main>
